import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { FormattedMessage, useIntl } from 'react-intl';
import axiosConfig from 'src/Resources/axiosConfig';
import {
  TicketSummary,
  TicketStatus,
  TicketPriority,
  categoryKeys,
  subCategoryKeys,
  getCategoryDisplayLabel,
  getSubCategoryDisplayLabel
} from 'src/interfaces/TicketTypes';
import Footer from 'src/components/Footer';
import CreateTicketModal from './CreateTicketModal';
import StatusChip from './StatusChip';

const statusKeys: Record<number, { id: string; defaultMessage: string }> = {
  [TicketStatus.Open]:       { id: 'statusOpen',       defaultMessage: 'Open' },
  [TicketStatus.InProgress]: { id: 'statusInProgress', defaultMessage: 'In Progress' },
  [TicketStatus.Resolved]:   { id: 'statusResolved',   defaultMessage: 'Solved' }
};

const priorityKeys: Record<number, { id: string; defaultMessage: string }> = {
  [TicketPriority.Critical]: { id: 'priorityCritical', defaultMessage: 'Critical' },
  [TicketPriority.High]:     { id: 'priorityHigh',     defaultMessage: 'High' },
  [TicketPriority.Medium]:   { id: 'priorityMedium',   defaultMessage: 'Medium' },
  [TicketPriority.Low]:      { id: 'priorityLow',      defaultMessage: 'Low' }
};

function TicketList() {
  const navigate = useNavigate();
  const intl = useIntl();
  const [tickets, setTickets] = useState<TicketSummary[]>([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<number | ''>('');
  const [createOpen, setCreateOpen] = useState(false);
  const [error, setError] = useState('');

  const fetchTickets = () => {
    axiosConfig
      .get('/GetTicketSummaries')
      .then((res) => setTickets(res.data))
      .catch(() => setError('Failed to load tickets'));
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const filtered = tickets
    .filter((t) => {
      const matchesSearch =
        search === '' ||
        t.subject.toLowerCase().includes(search.toLowerCase()) ||
        t.installationName.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = statusFilter === '' || t.status === statusFilter;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  return (
    <div style={{ userSelect: 'none' }}>
      <Container maxWidth="xl" sx={{ marginTop: '20px' }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h3">
            <FormattedMessage id="tickets" defaultMessage="Tickets" />
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
          >
            <FormattedMessage
              id="createTicket"
              defaultMessage="Create Ticket"
            />
          </Button>
        </Box>

        <Box display="flex" gap={2} mb={2}>
          <TextField
            size="small"
            label={<FormattedMessage id="search" defaultMessage="Search" />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 250 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>
              <FormattedMessage id="status" defaultMessage="Status" />
            </InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) =>
                setStatusFilter(e.target.value === '' ? '' : Number(e.target.value))
              }
            >
              <MenuItem value="">
                <FormattedMessage
                  id="allStatuses"
                  defaultMessage="All Statuses"
                />
              </MenuItem>
              {Object.entries(statusKeys).map(([val, msg]) => (
                <MenuItem key={val} value={Number(val)}>
                  {intl.formatMessage(msg)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {filtered.length === 0 && !error ? (
          <Alert severity="info">
            <FormattedMessage
              id="noTickets"
              defaultMessage="No tickets found."
            />
          </Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>
                    <FormattedMessage
                      id="subject"
                      defaultMessage="Subject"
                    />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage
                      id="status"
                      defaultMessage="Status"
                    />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage
                      id="priority"
                      defaultMessage="Priority"
                    />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage
                      id="category"
                      defaultMessage="Category"
                    />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage
                      id="installation"
                      defaultMessage="Installation"
                    />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage
                      id="createdAt"
                      defaultMessage="Created"
                    />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((ticket) => {
                  const catLabel = getCategoryDisplayLabel(ticket.category, ticket.customCategory);
                  const catKey = categoryKeys[ticket.category];
                  const catDisplay = catKey
                    ? intl.formatMessage(catKey)
                    : catLabel;
                  const subLabel = getSubCategoryDisplayLabel(ticket.subCategory, ticket.customSubCategory);
                  const subKey = subCategoryKeys[ticket.subCategory];
                  const subDisplay = subKey
                    ? intl.formatMessage(subKey)
                    : subLabel;

                  return (
                    <TableRow key={ticket.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/tickets/${ticket.id}`)}>
                      <TableCell>{ticket.id}</TableCell>
                      <TableCell>{ticket.subject}</TableCell>
                      <TableCell>
                        <StatusChip status={ticket.status} />
                      </TableCell>
                      <TableCell>{intl.formatMessage(priorityKeys[ticket.priority] ?? { id: 'unknown', defaultMessage: '-' })}</TableCell>
                      <TableCell>
                        {ticket.customCategory
                          ? ticket.customCategory
                          : catDisplay}
                        {subLabel !== 'Other' && subLabel !== 'Unknown' && subLabel !== 'General'
                          ? ` — ${ticket.customSubCategory || subDisplay}`
                          : ticket.customSubCategory
                            ? ` — ${ticket.customSubCategory}`
                            : ''}
                      </TableCell>
                      <TableCell>{ticket.installationName}</TableCell>
                      <TableCell>
                        {new Date(ticket.createdAt).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <CreateTicketModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={fetchTickets}
        />
      </Container>
      <Footer />
    </div>
  );
}

export default TicketList;
